import {
  Card,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import UploadField from "components/FormField/FileUploadField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { useGetAllContent, useUpdateContents, useUploadContentImage } from "../content.hook";
import { Content, ETypeContent } from "../content.type";

const APP_ROUTES = [
  { label: "Accueil", value: "index" },
  { label: "Programmation", value: "Programming" },
  { label: "Cartes", value: "Cartes" },
  { label: "Foire aux questions", value: "Faq" },
  { label: "Bénévolat", value: "Volunteer" },
  { label: "Notifications", value: "screens/Notifications" },
  { label: "Billetterie", value: "screens/BuyTickets" },
  { label: "Éco H2O", value: "screens/EcoH2o" },
  { label: "Hébergement", value: "screens/Hebergement" },
  { label: "Musique", value: "screens/programs/Musiq" },
  { label: "Sports", value: "screens/programs/Sports" },
  { label: "Activités hors murs", value: "screens/programs/Activities" },
  { label: "Famille", value: "screens/programs/FreeActivities" },
  { label: "Cartes détaillées", value: "screens/maps/Maps" },
];

type LinkMode = "internal" | "external";

function LinkField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const isInternal = !!value && !value.startsWith("http");
  const [mode, setMode] = useState<LinkMode>(isInternal ? "internal" : "external");

  const handleModeChange = (_: any, newMode: LinkMode | null) => {
    if (!newMode) return;
    setMode(newMode);
    onChange("");
  };

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" fontWeight="regular">
        {label}
      </MDTypography>
      <MDBox mt={0.5}>
        <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
          <ToggleButton value="external">URL externe</ToggleButton>
          <ToggleButton value="internal">Page interne</ToggleButton>
        </ToggleButtonGroup>
      </MDBox>
      <MDBox mt={1}>
        {mode === "external" ? (
          <input
            type="text"
            placeholder="https://..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
            }}
          />
        ) : (
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          >
            <MenuItem value=""><em>Choisir une page</em></MenuItem>
            {APP_ROUTES.map((route) => (
              <MenuItem key={route.value} value={route.value}>
                {route.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </MDBox>
    </MDBox>
  );
}

const ECO_SECTIONS = [
  { type: ETypeContent.eco1, number: 1 },
  { type: ETypeContent.eco2, number: 2 },
  { type: ETypeContent.eco3, number: 3 },
  { type: ETypeContent.eco4, number: 4 },
];

type SectionValues = {
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
  labelButtonFr: string;
  labelButtonEn: string;
  linkButtonFr: string;
  linkButtonEn: string;
  image: string;
  imageFile?: File;
};

type InitialValues = {
  introTitleFr: string;
  introTitleEn: string;
  introBodyFr: string;
  introBodyEn: string;
  sections: SectionValues[];
};

function ContentUpdateEco(): JSX.Element {
  const { data, isLoading } = useGetAllContent();
  const updateContents = useUpdateContents();
  const uploadContentImage = useUploadContentImage();

  const intro = data?.find((c) => c.id === ETypeContent.ecoIntro);

  const initialValues: InitialValues = {
    introTitleFr: intro?.title_fr || "",
    introTitleEn: intro?.title_en || "",
    introBodyFr: intro?.body_fr || "",
    introBodyEn: intro?.body_en || "",
    sections: ECO_SECTIONS.map(({ type }) => {
      const existing = data?.find((c) => c.id === type);
      return {
        titleFr: existing?.title_fr || "",
        titleEn: existing?.title_en || "",
        bodyFr: existing?.body_fr || "",
        bodyEn: existing?.body_en || "",
        labelButtonFr: existing?.subBody_fr || "",
        labelButtonEn: existing?.subBody_en || "",
        linkButtonFr: existing?.link1 || "",
        linkButtonEn: existing?.link2 || "",
        image: existing?.image || "",
      };
    }),
  };

  const validations = Yup.object().shape({
    introTitleFr: Yup.string().required("champ obligatoire"),
    introTitleEn: Yup.string().required("champ obligatoire"),
  });

  const handleSubmit = async (values: InitialValues, { resetForm }: any) => {
    try {
      const contents: Content[] = [
        {
          id: ETypeContent.ecoIntro,
          title_fr: values.introTitleFr,
          title_en: values.introTitleEn,
          body_fr: values.introBodyFr,
          body_en: values.introBodyEn,
        },
        ...ECO_SECTIONS.map(({ type }, idx) => ({
          id: type,
          title_fr: values.sections[idx].titleFr,
          title_en: values.sections[idx].titleEn,
          body_fr: values.sections[idx].bodyFr,
          body_en: values.sections[idx].bodyEn,
          subBody_fr: values.sections[idx].labelButtonFr,
          subBody_en: values.sections[idx].labelButtonEn,
          link1: values.sections[idx].linkButtonFr,
          link2: values.sections[idx].linkButtonEn,
        })),
      ];
      await updateContents.mutateAsync(contents);

      // Upload images
      for (let idx = 0; idx < ECO_SECTIONS.length; idx++) {
        const file = values.sections[idx].imageFile;
        if (file) {
          await uploadContentImage.mutateAsync({ id: ECO_SECTIONS[idx].type, file });
        }
      }

      resetForm({ values });
    } catch (error: any) {
      console.error("error:", error);
      alert(error);
    }
  };

  return (
    <>
      {isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress color="inherit" />
        </MDBox>
      ) : (
        <MDBox mt={4}>
          <Card style={{ overflowY: "auto" }}>
            <Formik
              initialValues={initialValues}
              validationSchema={validations}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, dirty, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <MDBox my={3} m={4}>
                    {/* Titre page */}
                    <MDBox mb={4}>
                      <MDTypography variant="h4" fontWeight="medium">
                        Éco H2O
                      </MDTypography>
                    </MDBox>

                    {/* Introduction */}
                    <MDBox mt={3}>
                      <MDTypography variant="h5" fontWeight="regular" mb={2}>
                        Introduction
                      </MDTypography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Titre - FR"
                            name="introTitleFr"
                            value={values.introTitleFr}
                            error={errors.introTitleFr && touched.introTitleFr}
                            success={values.introTitleFr?.length > 0 && !errors.introTitleFr}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Titre - EN"
                            name="introTitleEn"
                            value={values.introTitleEn}
                            error={errors.introTitleEn && touched.introTitleEn}
                            success={values.introTitleEn?.length > 0 && !errors.introTitleEn}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Description - FR"
                            name="introBodyFr"
                            value={values.introBodyFr}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Description - EN"
                            name="introBodyEn"
                            value={values.introBodyEn}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>

                    {/* Sections numérotées */}
                    {ECO_SECTIONS.map(({ number }, idx) => (
                      <MDBox
                        key={idx}
                        mt={4}
                        bgColor="#EEEEEE"
                        borderRadius={5}
                        p={3}
                      >
                        <MDTypography variant="h5" fontWeight="medium" mb={2}>
                          Section {number}
                        </MDTypography>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type="text"
                              label="Titre - FR"
                              name={`sections.${idx}.titleFr`}
                              value={values.sections[idx].titleFr}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type="text"
                              label="Titre - EN"
                              name={`sections.${idx}.titleEn`}
                              value={values.sections[idx].titleEn}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type="text"
                              label="Description - FR"
                              name={`sections.${idx}.bodyFr`}
                              value={values.sections[idx].bodyFr}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type="text"
                              label="Description - EN"
                              name={`sections.${idx}.bodyEn`}
                              value={values.sections[idx].bodyEn}
                            />
                          </Grid>
                        </Grid>

                        {/* Icône */}
                        <MDBox mt={3}>
                          <MDTypography variant="caption" color="text" fontWeight="regular">
                            Icône
                          </MDTypography>
                          <MDBox mt={1}>
                            <UploadField
                              onUpload={(file: File) =>
                                setFieldValue(`sections.${idx}.imageFile`, file)
                              }
                              newUploadLabel="Ajouter une icône"
                              updateUploadLabel="Modifier l'icône"
                              noFilelabel="Aucune icône téléversée"
                              seeFileLabel="Voir l'icône actuelle"
                              initialUrl={values.sections[idx].image}
                            />
                          </MDBox>
                        </MDBox>

                        {/* Bouton */}
                        <MDBox mt={3}>
                          <MDTypography variant="caption" color="text" fontWeight="regular" mb={1}>
                            Bouton (laisser vide pour ne pas afficher)
                          </MDTypography>
                          <Grid container spacing={3} mt={0}>
                            <Grid item xs={12} sm={6}>
                              <FormField
                                type="text"
                                label="Label bouton - FR"
                                name={`sections.${idx}.labelButtonFr`}
                                value={values.sections[idx].labelButtonFr}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormField
                                type="text"
                                label="Label bouton - EN"
                                name={`sections.${idx}.labelButtonEn`}
                                value={values.sections[idx].labelButtonEn}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LinkField
                                label="Lien de redirection bouton - FR"
                                value={values.sections[idx].linkButtonFr}
                                onChange={(val) => setFieldValue(`sections.${idx}.linkButtonFr`, val)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LinkField
                                label="Lien de redirection bouton - EN"
                                value={values.sections[idx].linkButtonEn}
                                onChange={(val) => setFieldValue(`sections.${idx}.linkButtonEn`, val)}
                              />
                            </Grid>
                          </Grid>
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>

                  <MDBox p={2} display="flex" justifyContent="flex-end">
                    <MDButton
                      disabled={!dirty || isSubmitting}
                      type="submit"
                      variant="gradient"
                      color="info"
                    >
                      Valider
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: "inherit",
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      )}
                    </MDButton>
                  </MDBox>
                </Form>
              )}
            </Formik>
          </Card>
        </MDBox>
      )}
    </>
  );
}

export default ContentUpdateEco;
